// U32b (29/10) — bước gộp «Prompt & Video» (ô 7 của bảng): logic THUẦN, KHÔNG import antd (jest CRA không nạp được antd v6 + router v7).
// Dữ liệu chỉ lấy từ cái FE đã có: `workflow-status.events[]` (steps + thẻ) và chi tiết video (`FilmEvent`). Không bịa giá, không gọi API.
import type { FilmEvent } from '../../types/film';
import type { WorkflowEventStatus } from '../../types/filmWorkflow';
import { groupByScene, truncateText, type StateTone } from './workflowBoardUtils';
import { videoRowStatusOf, type VideoRowKind } from './videoStepRowUtils';
import { splitPieces, type RefChoice, type RefKind } from './promptChipUtils';

export { mergedStateOf, mergedSummaryOf } from './workflowBoardUtils';

export type VideoListKind = 'DONE' | 'RUNNING' | 'ERROR' | 'ATTN' | 'TODO';
export interface VideoListStatus {
  kind: VideoListKind;
  /** Nhãn đọc được (aria / tooltip) — tiếng Việt, không mã thô. */
  label: string;
  /** Chữ cho `StatusIcon` (luật của workflowStatusIcons chọn biểu tượng). */
  chipText: string;
  tone: StateTone;
  /** Trạng thái chi tiết của clip (nguồn videoRowStatusOf) — nút Gen đọc cái này. */
  rowKind: VideoRowKind;
  noPrompt: boolean;
}

/** Biểu tượng mục video: ✓ có clip chọn · ⏳ đang tạo (clip hoặc prompt) · ✕ lỗi · ! clip cũ / chờ chọn · ○ chưa có prompt hoặc clip. */
export function videoListStatusOf(event: WorkflowEventStatus, ev?: FilmEvent | null): VideoListStatus {
  const r = videoRowStatusOf(event, ev);
  const base = { rowKind: r.kind, noPrompt: r.noPrompt };
  const promptState = event.steps?.PROMPT ?? 'NOT_READY';
  switch (r.kind) {
    case 'SELECTED': return { ...base, kind: 'DONE', label: 'Đã có clip', chipText: 'xong', tone: 'ok' };
    case 'RUNNING': return { ...base, kind: 'RUNNING', label: 'Đang tạo clip', chipText: r.chipText, tone: 'doing' };
    case 'ERROR': return { ...base, kind: 'ERROR', label: r.headline, chipText: 'lỗi', tone: 'bad' };
    case 'STALE': return { ...base, kind: 'ATTN', label: 'Clip đã cũ — gen lại', chipText: 'cũ', tone: 'warn' };
    case 'UNPICKED': return { ...base, kind: 'ATTN', label: 'Có clip, chưa chọn bản', chipText: 'chờ chọn', tone: 'warn' };
    default:
      if (promptState === 'RUNNING') return { ...base, kind: 'RUNNING', label: 'Đang dựng prompt', chipText: 'đang chạy', tone: 'doing' };
      if (promptState === 'ERROR') return { ...base, kind: 'ERROR', label: 'Dựng prompt lỗi', chipText: 'lỗi', tone: 'bad' };
      return { ...base, kind: 'TODO', label: r.noPrompt ? 'Chưa có prompt' : 'Chưa có clip', chipText: 'chưa có', tone: 'todo' };
  }
}

const sortEvents = (events: WorkflowEventStatus[]): WorkflowEventStatus[] => [...events].sort((a, b) => a.sceneOrder - b.sceneOrder || a.eventOrder - b.eventOrder);

/** Video mặc định = video ĐẦU TIÊN cần xử lý (lỗi · cũ · chờ chọn · chưa có); không có việc ⇒ video đang chạy đầu tiên, rồi video đầu tiên. */
export function defaultVideoOf(events: WorkflowEventStatus[], evOf: (eventId: string) => FilmEvent | null | undefined): string | null {
  const sorted = sortEvents(events ?? []);
  if (!sorted.length) return null;
  const kindOf = (e: WorkflowEventStatus) => videoListStatusOf(e, evOf(e.eventId) ?? null).kind;
  const need = sorted.find((e) => ['ERROR', 'ATTN', 'TODO'].includes(kindOf(e)));
  return (need ?? sorted.find((e) => kindOf(e) === 'RUNNING') ?? sorted[0]).eventId;
}

/** `?event=` còn là video của plan ⇒ giữ; sai / vắng ⇒ mặc định. */
export function resolveSelectedVideoOf(events: WorkflowEventStatus[], requested: string | null | undefined, evOf: (eventId: string) => FilmEvent | null | undefined): string | null {
  if (requested && (events ?? []).some((e) => e.eventId === requested)) return requested;
  return defaultVideoOf(events, evOf);
}

export interface SceneListGroup { sceneId: string; title: string; heading: string; events: WorkflowEventStatus[] }
/** Cột trái: nhóm theo cảnh — «CẢNH n» + heading ngắn (≤ 40 ký tự, thiếu ⇒ rỗng). */
export function sceneListOf(events: WorkflowEventStatus[]): SceneListGroup[] {
  const raw = new Map(events.map((e) => [e.sceneId, (e.sceneHeading ?? '').trim()]));
  return groupByScene(events).map((g) => ({ sceneId: g.sceneId, title: `CẢNH ${g.sceneOrder}`, heading: truncateText(raw.get(g.sceneId) ?? '', 40), events: g.events }));
}

export interface PromptChip { key: string; kind: RefKind; label: string; url: string | null; ready: boolean; missing: boolean }
/** Dải chip ref = các tham chiếu CÓ trong prompt (token `@[Nhãn](kind:id)`), mỗi cái một lần. Token trỏ thực thể không còn trong bank ⇒ vẫn hiện, đánh dấu thiếu. */
export function promptChipsOf(prompt: string | null | undefined, choices: RefChoice[]): PromptChip[] {
  const byKey = new Map(choices.map((c) => [`${c.kind}:${c.id}`, c]));
  const out: PromptChip[] = [];
  const seen = new Set<string>();
  for (const p of splitPieces(String(prompt ?? ''))) {
    if (p.type !== 'token') continue;
    const key = `${p.kind}:${p.id}`;
    if (seen.has(key)) continue;
    seen.add(key);
    const known = byKey.get(key);
    out.push(known
      ? { key, kind: p.kind, label: known.label || p.label, url: known.previewUrl ?? null, ready: known.ready, missing: false }
      : { key, kind: p.kind, label: p.label, url: null, ready: false, missing: true });
  }
  return out;
}

export interface GenButton { enabled: boolean; label: string; why?: string }
/**
 * Nút «Gen» của MỘT video = đúng runner «Gen video này» (scope «còn thiếu»): chỉ có việc khi video chưa có clip dùng được (chưa tạo / lỗi / cũ).
 * Đã có clip dùng được ⇒ tắt kèm hướng dẫn: sửa prompt hoặc đổi engine thì clip thành «cũ» và nút bật lại. Giá chỉ hiện ở hộp xác nhận.
 */
export function genButtonOf(st: VideoListStatus): GenButton {
  switch (st.rowKind) {
    case 'NONE': return st.noPrompt ? { enabled: false, label: 'Gen', why: 'Chưa có prompt — tạo prompt trước.' } : { enabled: true, label: 'Gen' };
    case 'ERROR': return { enabled: true, label: 'Gen lại' };
    case 'STALE': return { enabled: true, label: 'Gen lại' };
    case 'RUNNING': return { enabled: false, label: 'Gen', why: 'Đang tạo clip…' };
    default: return { enabled: false, label: 'Gen lại', why: 'Đã có clip dùng được — sửa prompt hoặc đổi engine thì clip thành «cũ» và bấm Gen lại.' };
  }
}

/** MỘT dòng tóm tắt đầu bước: «3/9 video có clip · 4 chưa có prompt · 1 lỗi» (phần bằng 0 thì bỏ). */
export function promptVideoSummaryOf(events: WorkflowEventStatus[], evOf: (eventId: string) => FilmEvent | null | undefined): string {
  const list = events ?? [];
  if (!list.length) return '';
  const st = list.map((e) => videoListStatusOf(e, evOf(e.eventId) ?? null));
  const withClip = st.filter((x) => x.kind === 'DONE' || x.rowKind === 'STALE').length;
  const noPrompt = st.filter((x) => x.noPrompt && x.kind === 'TODO').length;
  const failed = st.filter((x) => x.kind === 'ERROR').length;
  return [`${withClip}/${list.length} video có clip`, noPrompt ? `${noPrompt} chưa có prompt` : '', failed ? `${failed} lỗi` : ''].filter(Boolean).join(' · ');
}
